import React from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';

import { Text, SegmentedButtons, Switch, useTheme } from '@/components/ui';
import { RecurrenceType, WeekDay, MonthlyRecurrenceType, type RecurrenceRule } from '@/types/models';
import { describeRecurrence } from '@/utils/recurring-task-calculator';

interface RecurrenceEditorProps {
	recurrenceRule: RecurrenceRule;
	onChange: (rule: RecurrenceRule) => void;
	disabled?: boolean;
}

const WEEK_DAYS = [
	{ label: 'S', value: WeekDay.Sunday },
	{ label: 'M', value: WeekDay.Monday },
	{ label: 'T', value: WeekDay.Tuesday },
	{ label: 'W', value: WeekDay.Wednesday },
	{ label: 'T', value: WeekDay.Thursday },
	{ label: 'F', value: WeekDay.Friday },
	{ label: 'S', value: WeekDay.Saturday },
];

const ORDINALS = ['1st', '2nd', '3rd', '4th', 'Last'];

export const RecurrenceEditor: React.FC<RecurrenceEditorProps> = ({
	recurrenceRule,
	onChange,
	disabled = false,
}) => {
	const theme = useTheme();

	const updateRule = (updates: Partial<RecurrenceRule>) => {
		onChange({ ...recurrenceRule, ...updates });
	};

	const toggleDayOfWeek = (day: WeekDay) => {
		const currentDays = recurrenceRule.daysOfWeek || [];
		const newDays = currentDays.includes(day)
			? currentDays.filter(d => d !== day)
			: [...currentDays, day];
		updateRule({ daysOfWeek: newDays });
	};

	if (recurrenceRule.type === RecurrenceType.None) {
		return null;
	}

	return (
		<View style={[styles.container, { backgroundColor: theme.colors.surface }]}>
			{/* Header */}
			<View style={styles.header}>
				<Text style={[styles.title, { color: theme.colors.onSurface }]}>
					Repeats
				</Text>
				<Text style={[styles.description, { color: theme.colors.onSurfaceVariant }]}>
					{describeRecurrence(recurrenceRule)}
				</Text>
			</View>

			{/* Recurrence Type */}
			<View style={styles.section}>
				<Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
					Repeats
				</Text>
				<SegmentedButtons
					buttons={[
						{ value: RecurrenceType.Daily, label: 'Daily' },
						{ value: RecurrenceType.Weekly, label: 'Weekly' },
						{ value: RecurrenceType.Monthly, label: 'Monthly' },
						{ value: RecurrenceType.Yearly, label: 'Yearly' },
					]}
					value={recurrenceRule.type}
					onValueChange={(value) => updateRule({ type: value as RecurrenceType })}
				/>
			</View>

			{/* Interval */}
			<View style={styles.section}>
				<Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
					Every
				</Text>
				<View style={styles.intervalRow}>
					<TouchableOpacity
						style={[styles.intervalButton, { borderColor: theme.colors.outline }]}
						onPress={() => updateRule({ interval: Math.max(1, recurrenceRule.interval - 1) })}
						disabled={disabled || recurrenceRule.interval <= 1}
					>
						<Text style={[styles.intervalText, { color: theme.colors.primary }]}>-</Text>
					</TouchableOpacity>
					
					<View style={[styles.intervalValue, { backgroundColor: theme.colors.surfaceVariant }]}>
						<Text style={[styles.intervalNumber, { color: theme.colors.onSurface }]}>
							{recurrenceRule.interval}
						</Text>
					</View>
					
					<TouchableOpacity
						style={[styles.intervalButton, { borderColor: theme.colors.outline }]}
						onPress={() => updateRule({ interval: recurrenceRule.interval + 1 })}
						disabled={disabled}
					>
						<Text style={[styles.intervalText, { color: theme.colors.primary }]}>+</Text>
					</TouchableOpacity>
					
					<Text style={[styles.intervalLabel, { color: theme.colors.onSurfaceVariant }]}>
						{getIntervalLabel(recurrenceRule.type, recurrenceRule.interval)}
					</Text>
				</View>
			</View>

			{/* Weekly Days */}
			{recurrenceRule.type === RecurrenceType.Weekly && (
				<View style={styles.section}>
					<Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
						On these days
					</Text>
					<View style={styles.weekDaysRow}>
						{WEEK_DAYS.map(({ label, value }) => (
							<TouchableOpacity
								key={value}
								style={[
									styles.dayButton,
									{
										backgroundColor: recurrenceRule.daysOfWeek?.includes(value)
											? theme.colors.primary
											: theme.colors.surfaceVariant,
										borderColor: theme.colors.outline,
									},
								]}
								onPress={() => toggleDayOfWeek(value)}
								disabled={disabled}
							>
								<Text
									style={[
										styles.dayText,
										{
											color: recurrenceRule.daysOfWeek?.includes(value)
												? theme.colors.onPrimary
												: theme.colors.onSurfaceVariant,
										},
									]}
								>
									{label}
								</Text>
							</TouchableOpacity>
						))}
					</View>
				</View>
			)}

			{/* Monthly Options */}
			{recurrenceRule.type === RecurrenceType.Monthly && (
				<View style={styles.section}>
					<Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
						Repeat on
					</Text>
					
					<SegmentedButtons
						buttons={[
							{ value: MonthlyRecurrenceType.ByDate, label: 'Date' },
							{ value: MonthlyRecurrenceType.ByDay, label: 'Day of week' },
						]}
						value={recurrenceRule.monthlyType || MonthlyRecurrenceType.ByDate}
						onValueChange={(value) => updateRule({ monthlyType: value as MonthlyRecurrenceType })}
					/>

					{(!recurrenceRule.monthlyType || recurrenceRule.monthlyType === MonthlyRecurrenceType.ByDate) && (
						<View style={styles.dateOption}>
							<Text style={[styles.optionLabel, { color: theme.colors.onSurfaceVariant }]}>
								Day
							</Text>
							<TouchableOpacity
								style={[styles.dateButton, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.outline }]}
								onPress={() => {
									// Show date picker or increment logic
									const currentDay = recurrenceRule.dayOfMonth || 15;
									const nextDay = currentDay >= 31 ? 1 : currentDay + 1;
									updateRule({ dayOfMonth: nextDay });
								}}
								disabled={disabled}
							>
								<Text style={[styles.dateText, { color: theme.colors.onSurface }]}>
									{recurrenceRule.dayOfMonth || 15}
								</Text>
							</TouchableOpacity>
						</View>
					)}

					{recurrenceRule.monthlyType === MonthlyRecurrenceType.ByDay && (
						<View style={styles.dayOfWeekOption}>
							<Text style={[styles.optionLabel, { color: theme.colors.onSurfaceVariant }]}>
								The
							</Text>
							<ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.ordinalScroll}>
								{ORDINALS.map((ordinal, index) => (
									<TouchableOpacity
										key={ordinal}
										style={[
											styles.ordinalButton,
											{
												backgroundColor: recurrenceRule.weekOfMonth === index + 1
													? theme.colors.primary
													: theme.colors.surfaceVariant,
												borderColor: theme.colors.outline,
											},
										]}
										onPress={() => updateRule({ weekOfMonth: index + 1 })}
										disabled={disabled}
									>
										<Text
											style={[
												styles.ordinalText,
												{
													color: recurrenceRule.weekOfMonth === index + 1
														? theme.colors.onPrimary
														: theme.colors.onSurfaceVariant,
												},
											]}
										>
											{ordinal}
										</Text>
									</TouchableOpacity>
								))}
							</ScrollView>
							
							<ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.weekdayScroll}>
								{WEEK_DAYS.filter((_, index) => index !== 0 && index !== 6).map(({ label, value }) => (
									<TouchableOpacity
										key={value}
										style={[
											styles.weekdayButton,
											{
												backgroundColor: recurrenceRule.dayOfWeekForMonth === value
													? theme.colors.primary
													: theme.colors.surfaceVariant,
												borderColor: theme.colors.outline,
											},
										]}
										onPress={() => updateRule({ dayOfWeekForMonth: value })}
										disabled={disabled}
									>
										<Text
											style={[
												styles.weekdayButtonText,
												{
													color: recurrenceRule.dayOfWeekForMonth === value
														? theme.colors.onPrimary
														: theme.colors.onSurfaceVariant,
												},
											]}
										>
											{value.slice(0, 3)}
										</Text>
									</TouchableOpacity>
								))}
							</ScrollView>
						</View>
					)}
				</View>
			)}

			{/* End Options */}
			<View style={styles.section}>
				<Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
					Ends
				</Text>
				
				<View style={styles.endOption}>
					<Switch
						value={!!recurrenceRule.endDate}
						onValueChange={(enabled) => {
							if (enabled) {
								updateRule({ endDate: Date.now() + 90 * 24 * 60 * 60 * 1000 }); // Default 90 days
							} else {
								updateRule({ endDate: undefined });
							}
						}}
						disabled={disabled}
					/>
					<Text style={[styles.endLabel, { color: theme.colors.onSurface }]}>
						On specific date
					</Text>
				</View>

				{recurrenceRule.endDate && (
					<TouchableOpacity
						style={[styles.endDateButton, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.outline }]}
						onPress={() => {
							// Date picker logic here
							const newDate = new Date(recurrenceRule.endDate!);
							newDate.setDate(newDate.getDate() + 7);
							updateRule({ endDate: newDate.getTime() });
						}}
						disabled={disabled}
					>
						<Text style={[styles.endDateText, { color: theme.colors.onSurface }]}>
							{new Date(recurrenceRule.endDate).toLocaleDateString()}
						</Text>
					</TouchableOpacity>
				)}

				<View style={styles.endOption}>
					<Switch
						value={!!recurrenceRule.maxOccurrences}
						onValueChange={(enabled) => {
							if (enabled) {
								updateRule({ maxOccurrences: 10 }); // Default 10 occurrences
							} else {
								updateRule({ maxOccurrences: undefined });
							}
						}}
						disabled={disabled}
					/>
					<Text style={[styles.endLabel, { color: theme.colors.onSurface }]}>
						After
					</Text>
					{recurrenceRule.maxOccurrences && (
						<Text style={[styles.endCount, { color: theme.colors.primary }]}>
							{recurrenceRule.maxOccurrences} occurrences
						</Text>
					)}
				</View>
			</View>
		</View>
	);
};

function getIntervalLabel(type: RecurrenceType, interval: number): string {
	switch (type) {
		case RecurrenceType.Daily:
			return interval === 1 ? 'day' : 'days';
		case RecurrenceType.Weekly:
			return interval === 1 ? 'week' : 'weeks';
		case RecurrenceType.Monthly:
			return interval === 1 ? 'month' : 'months';
		case RecurrenceType.Yearly:
			return interval === 1 ? 'year' : 'years';
		default:
			return '';
	}
}

const styles = StyleSheet.create({
	container: {
		borderRadius: 12,
		padding: 16,
		marginVertical: 8,
	},
	header: {
		marginBottom: 16,
	},
	title: {
		fontSize: 16,
		fontWeight: '600',
		marginBottom: 4,
	},
	description: {
		fontSize: 13,
	},
	section: {
		marginBottom: 16,
	},
	sectionTitle: {
		fontSize: 14,
		fontWeight: '500',
		marginBottom: 8,
	},
	intervalRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
	},
	intervalButton: {
		width: 36,
		height: 36,
		borderRadius: 18,
		borderWidth: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
	intervalText: {
		fontSize: 18,
		fontWeight: '600',
	},
	intervalValue: {
		width: 60,
		height: 40,
		borderRadius: 8,
		alignItems: 'center',
		justifyContent: 'center',
	},
	intervalNumber: {
		fontSize: 16,
		fontWeight: '600',
	},
	intervalLabel: {
		fontSize: 14,
	},
	weekDaysRow: {
		flexDirection: 'row',
		gap: 8,
	},
	dayButton: {
		width: 40,
		height: 40,
		borderRadius: 20,
		borderWidth: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
	dayText: {
		fontSize: 12,
		fontWeight: '600',
	},
	dateOption: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
	},
	optionLabel: {
		fontSize: 14,
	},
	dateButton: {
		width: 60,
		height: 40,
		borderRadius: 8,
		borderWidth: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
	dateText: {
		fontSize: 16,
		fontWeight: '600',
	},
	dayOfWeekOption: {
		marginTop: 12,
	},
	ordinalScroll: {
		marginHorizontal: 12,
	},
	ordinalButton: {
		paddingHorizontal: 12,
		paddingVertical: 8,
		borderRadius: 6,
		borderWidth: 1,
		marginRight: 8,
	},
	ordinalText: {
		fontSize: 12,
		fontWeight: '500',
	},
	weekdayScroll: {
		marginTop: 8,
	},
	weekdayButton: {
		paddingHorizontal: 12,
		paddingVertical: 8,
		borderRadius: 6,
		borderWidth: 1,
		marginRight: 8,
	},
	weekdayButtonText: {
		fontSize: 12,
		fontWeight: '500',
	},
	endOption: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		marginBottom: 8,
	},
	endLabel: {
		fontSize: 14,
	},
	endCount: {
		fontSize: 14,
		fontWeight: '500',
	},
	endDateButton: {
		padding: 12,
		borderRadius: 8,
		borderWidth: 1,
		alignItems: 'center',
		marginTop: 8,
	},
	endDateText: {
		fontSize: 14,
		fontWeight: '500',
	},
});